import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { EventApplication } from './types'
import { listEventsRequest, fetchEventCountsRequest, getEventByIdRequest, listApplicationsRequest, updateApplicationStatusRequest, bulkUpdateApplicationStatusRequest, directInviteRequest, sendEventMessageRequest, type ListEventsParams, type EventCountsParams, type SendMessagePayload } from './api'

export function useEvent(eventId: string) {
  return useQuery({
    queryKey: ['admin', 'events', eventId] as const,
    queryFn: () => getEventByIdRequest(eventId),
    enabled: !!eventId,
    staleTime: 60_000,
  })
}

async function fetchEvents(params: ListEventsParams): Promise<Awaited<ReturnType<typeof listEventsRequest>>> {
  return listEventsRequest(params)
}

export function useEvents(params: ListEventsParams = {}) {
  return useQuery({
    queryKey: ['admin', 'events', params] as const,
    queryFn: () => fetchEvents(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })
}

export function useEventCounts(params: EventCountsParams = {}) {
  return useQuery({
    queryKey: ['admin', 'events', 'counts', params] as const,
    queryFn: () => fetchEventCountsRequest(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })
}

export function useEventApplications(eventId: string) {
  return useQuery({
    queryKey: ['admin', 'events', eventId, 'applications'] as const,
    queryFn: () => listApplicationsRequest(eventId),
    enabled: !!eventId,
    staleTime: 15_000,
  })
}

export function useUpdateApplicationStatus(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ applicationId, status, notes, force }: { applicationId: string; status: string; notes?: string; force?: boolean }) =>
      updateApplicationStatusRequest(applicationId, { status, notes, force }),
    onSuccess: (updated: EventApplication) => {
      qc.setQueryData<EventApplication[]>(['admin', 'events', eventId, 'applications'], (old) =>
        old ? old.map((a) => (a._id === updated._id ? updated : a)) : old
      )
    },
  })
}

export function useBulkUpdateApplicationStatus(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: { applicationIds: string[]; status: string; notes?: string }) =>
      bulkUpdateApplicationStatusRequest(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'events', eventId, 'applications'] })
    },
  })
}

export function useDirectInvite(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: { userId: string; personalNote?: string }) =>
      directInviteRequest(eventId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'events', eventId, 'applications'] })
    },
  })
}

export function useSendEventMessage(eventId: string) {
  return useMutation({
    mutationFn: (payload: SendMessagePayload) =>
      sendEventMessageRequest(eventId, payload),
  })
}

// ── Check-in ──

export function useCheckInSearch(eventId: string, q: string) {
  return useQuery({
    queryKey: ['admin', 'events', eventId, 'check-in', q] as const,
    queryFn: () => import('./api').then((m) => m.checkInSearchRequest(eventId, q || undefined)),
    enabled: !!eventId,
    staleTime: 5_000,
    refetchInterval: 30_000,
  })
}

export function useCheckIn(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (applicationId: string) =>
      import('./api').then((m) => m.checkInRequest(applicationId)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'events', eventId, 'check-in'] })
    },
  })
}

export function useUncheckIn(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (applicationId: string) =>
      import('./api').then((m) => m.uncheckInRequest(applicationId)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'events', eventId, 'check-in'] })
    },
  })
}

export function useAddWalkIn(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: { name: string; email: string; phone?: string }) =>
      import('./api').then((m) => m.addWalkInRequest(eventId, payload)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'events', eventId, 'check-in'] })
    },
  })
}

export function useUpdateAttendeeType(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ applicationId, attendeeType }: { applicationId: string; attendeeType: import('./api').AttendeeType | null }) =>
      import('./api').then((m) => m.updateAttendeeTypeRequest(applicationId, attendeeType)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'events', eventId, 'check-in'] })
    },
  })
}

export function useUpdateAdminNotes(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ applicationId, adminNotes }: { applicationId: string; adminNotes: string }) =>
      import('./api').then((m) => m.updateAdminNotesRequest(applicationId, adminNotes)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'events', eventId, 'check-in'] })
    },
  })
}

export function useUpdateAttendeeDetails(eventId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ applicationId, payload }: { applicationId: string; payload: { name?: string; email?: string; phone?: string } }) =>
      import('./api').then((m) => m.updateAttendeeDetailsRequest(applicationId, payload)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'events', eventId, 'check-in'] })
    },
  })
}
